import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import "./DemoVideoPage.css";
import Header from "../../commonComponents/header/Header";

const VIDEO_CONFIGS = {
  coding: {
    id: "coding",
    roundLabel: "coding",
    title: "Coding round demo",
    description:
      "How to read the question, edit input, run your code, and submit your solution.",
    src: "/coding-demo-video.mp4",
  },
  whiteboarding: {
    id: "whiteboard",
    roundLabel: "whiteboard",
    title: "Whiteboard round demo",
    description:
      "How to read the question, navigate the canvas, use tools, and submit.",
    src: "/whiteboarding-demo-video.mp4",
  },
};

// How far (in seconds) a user may jump ahead of the furthest point watched
const SEEK_TOLERANCE = 1;
// Video must be played to within this many seconds of the end to count as watched
const END_TOLERANCE = 1.5;

function roundList(labels) {
  if (labels.length === 1) return labels[0];
  return labels.slice(0, -1).join(", ") + " and " + labels[labels.length - 1];
}

const DemoVideoPage = () => {
  const navigate = useNavigate();

  const hasCoding = sessionStorage.getItem("coding_env") === "true";
  const hasWhiteboarding =
    sessionStorage.getItem("whiteboarding_env") === "true";

  const videos = [
    ...(hasCoding ? [VIDEO_CONFIGS.coding] : []),
    ...(hasWhiteboarding ? [VIDEO_CONFIGS.whiteboarding] : []),
  ];

  const [watched, setWatched] = useState(() => new Set());
  const [seekWarning, setSeekWarning] = useState(false);
  const videoRefs = useRef({});
  const maxWatchedTime = useRef({}); // { [id]: furthest second reached by natural playback }
  const warningTimer = useRef(null);

  const showSeekWarning = () => {
    setSeekWarning(true);
    clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setSeekWarning(false), 3000);
  };

  useEffect(() => {
    return () => clearTimeout(warningTimer.current);
  }, []);

  // Track the furthest point reached through normal playback (not jumps)
  const handleTimeUpdate = (id) => {
    const vid = videoRefs.current[id];
    if (!vid || vid.seeking) return;

    const max = maxWatchedTime.current[id] || 0;
    if (
      vid.currentTime > max &&
      vid.currentTime - max <= SEEK_TOLERANCE + 0.5
    ) {
      maxWatchedTime.current[id] = vid.currentTime;
    }
  };

  // Snap back if the user tries to skip ahead of what they've watched
  const handleSeeking = (id) => {
    const vid = videoRefs.current[id];
    if (!vid || watched.has(id)) return; // free seeking once fully watched

    const max = maxWatchedTime.current[id] || 0;
    if (vid.currentTime > max + SEEK_TOLERANCE) {
      vid.currentTime = max;
      showSeekWarning();
    }
  };

  const handleVideoEnded = (id) => {
    const vid = videoRefs.current[id];
    const max = maxWatchedTime.current[id] || 0;

    // Only count as watched if they actually played through
    if (vid && vid.duration && max < vid.duration - END_TOLERANCE) {
      vid.currentTime = max;
      showSeekWarning();
      return;
    }

    setWatched((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // Block speeding through until the video has been fully watched
  const handleRateChange = (id) => {
    const vid = videoRefs.current[id];
    if (vid && !watched.has(id) && vid.playbackRate > 1) {
      vid.playbackRate = 1;
    }
  };

  // Backward seek is allowed, so maxWatchedTime is intentionally not reset
  const handleReplay = (id) => {
    const vid = videoRefs.current[id];
    if (vid) {
      vid.currentTime = 0;
      vid.play();
    }
  };

  const handlePlay = (activeId) => {
    Object.entries(videoRefs.current).forEach(([id, el]) => {
      if (id !== activeId && el && !el.paused) {
        el.pause();
      }
    });
  };

  const allWatched = videos.length === 0 || watched.size >= videos.length;

  const handleProceed = () => {
    if (allWatched) {
      navigate("/verification");
    }
  };

  useEffect(() => {
    if (!hasCoding && !hasWhiteboarding) {
      navigate("/verification", { replace: true });
    }
  }, [hasCoding, hasWhiteboarding, navigate]);

  if (!hasCoding && !hasWhiteboarding) return null;

  const labels = videos.map((v) => v.roundLabel);
  const roundsText = roundList(labels);
  const roundWord = videos.length === 1 ? "round" : "rounds";
  const demoWord = videos.length === 1 ? "demo" : "demos";
  const pronoun = videos.length === 1 ? "it" : "each";
  const introText = `After your main interview, you'll move into the ${roundsText} ${roundWord}. Watch the quick ${demoWord} below to see how ${
    pronoun === "it" ? "the workspace" : pronoun + " workspace"
  } works, then proceed when you're ready.`;

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden px-4 pt-4">
      {/* Header from AssessmentInegrity */}
      <Header />

      {/* Middle Content from demo.html */}
      <div className="flex-1 flex flex-col justify-center demo-content-area">
        {/* Page Head */}
        <div className="demo-page-head">
          <h1>Watch these quick walkthroughs before you begin</h1>
          <p id="introText">{introText}</p>
          <div className="demo-progress-row" id="progressRow">
            {videos.map((v) => (
              <span
                key={v.id}
                className={`demo-progress-dot${watched.has(v.id) ? " watched" : ""}`}
                id={`dot-${v.id}`}
              />
            ))}
            <span className="demo-progress-label" id="progressLabel">
              {watched.size} of {videos.length} watched
            </span>
          </div>
          {seekWarning && (
            <p
              role="alert"
              className="mt-2 text-[13px] text-red-600 font-medium"
            >
              Please watch the full video. Skipping ahead is disabled.
            </p>
          )}
        </div>

        {/* Video Grid from demo.html */}
        <div
          className={`video-grid${videos.length === 1 ? " single" : ""}`}
          id="gridWrap"
        >
          {videos.map((v) => (
            <div key={v.id} className="video-card">
              <div className="frame">
                <video
                  ref={(el) => {
                    videoRefs.current[v.id] = el;
                  }}
                  id={`vid-${v.id}`}
                  controls
                  controlsList="nodownload noplaybackrate"
                  preload="metadata"
                  disablePictureInPicture
                  onPlay={() => handlePlay(v.id)}
                  onTimeUpdate={() => handleTimeUpdate(v.id)}
                  onSeeking={() => handleSeeking(v.id)}
                  onRateChange={() => handleRateChange(v.id)}
                  onEnded={() => handleVideoEnded(v.id)}
                >
                  <source src={v.src} type="video/mp4" />
                </video>
              </div>
              <div className="card-meta">
                <div>
                  <h3>{v.title}</h3>
                  <p>{v.description}</p>
                </div>
                <span
                  className={`status-pill${watched.has(v.id) ? " watched" : ""}`}
                  id={`pill-${v.id}`}
                >
                  {watched.has(v.id) ? "Watched" : "Not watched"}
                </span>
              </div>
              <div className="card-actions">
                <button
                  className="btn-replay"
                  onClick={() => handleReplay(v.id)}
                  type="button"
                >
                  &#8635; Play again
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Proceed Button from AssessmentInegrity */}
      <div className="mt-auto w-full flex gap-2 justify-end pb-4">
        <button
          onClick={handleProceed}
          disabled={!allWatched}
          className={`flex items-center px-6 py-1.5 btn-size bg-btnBgColor text-white text-[16px] ${
            !allWatched ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          Proceed
          <img src={arrowImg} alt="arrow" className="ml-2" />
        </button>
      </div>

      {/* Footer from AssessmentInegrity */}
      <div className="w-full py-4 text-[10px] text-gray-600 text-center">
        ©{" "}
        {new Date().getFullYear() > 2026
          ? `2026-${new Date().getFullYear()}`
          : "2026"}
        , Needles and Haystacks Technology Pvt Ltd.
      </div>
    </div>
  );
};

export default DemoVideoPage;
